import {
  itemIconUrl,
  itemFloorUrl,
  itemManifestUrl,
} from "../adventure/ActiveAdventure.js";

/**
 * A05a — `ItemSpriteResolver`
 *
 * Two-image model per item:
 *   - `icon.png`  — 64×64 inventory cell / hand-button / tooltip icon
 *   - `floor.png` — 96×96 side-on "lying flat" sprite for floor drops
 *
 * Both images resolve adventure-scoped only — the active adventure
 * must ship its own copy of any item sprite it references. UI
 * consumers handle the missing case (procedural letter-tile / canvas)
 * when probe returns null. Per-item metadata lives in an optional
 * sibling `item.json` whose only meaningful field today is
 * `floor.mirror = false`, which suppresses the per-instance horizontal
 * flip the floor-drop renderer applies by default.
 *
 * Sibling to `SpriteManager` — same probe-and-cache discipline.
 */

// ── Manifest ─────────────────────────────────────────────────────────────────

export interface ItemSpriteManifest {
  readonly floor?: {
    /**
     * When false, suppresses the per-instance horizontal flip the
     * floor-drop renderer applies by default. Defaults to true.
     */
    readonly mirror?: boolean;
  };
}

export const DEFAULT_FLOOR_MIRROR = true;

// ── Probe machinery ──────────────────────────────────────────────────────────

type ProbeResult = string | null;

const iconCache = new Map<string, ProbeResult>();
const floorCache = new Map<string, ProbeResult>();
const manifestCache = new Map<string, ItemSpriteManifest | null>();
const inflightIcon = new Map<string, Promise<ProbeResult>>();
const inflightFloor = new Map<string, Promise<ProbeResult>>();
const inflightManifest = new Map<string, Promise<ItemSpriteManifest | null>>();

type ProbeFn = (url: string) => Promise<boolean>;
type FetchFn = (url: string) => Promise<string | null>;

let probeOverride: ProbeFn | null = null;
let fetchOverride: FetchFn | null = null;

/** Test seam — replace the default `Image` probe with a stub. */
export function _setProbeForTests(p: ProbeFn | null): void {
  probeOverride = p;
  iconCache.clear();
  floorCache.clear();
  inflightIcon.clear();
  inflightFloor.clear();
}

/** Test seam — replace the default `fetch` for `item.json`. */
export function _setManifestFetchForTests(f: FetchFn | null): void {
  fetchOverride = f;
  manifestCache.clear();
  inflightManifest.clear();
}

/** Drop all caches. Useful when the active adventure changes. */
export function resetItemSpriteResolver(): void {
  iconCache.clear();
  floorCache.clear();
  manifestCache.clear();
  inflightIcon.clear();
  inflightFloor.clear();
  inflightManifest.clear();
}

function defaultProbe(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

async function probe(url: string): Promise<boolean> {
  return (probeOverride ?? defaultProbe)(url);
}

async function probeFirst(candidates: readonly string[]): Promise<ProbeResult> {
  for (const url of candidates) {
    if (await probe(url)) return url;
  }
  return null;
}

async function defaultFetchManifest(url: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Probe for `<item-id>/icon.png` adventure-scoped, return the URL or
 * `null` when the adventure doesn't ship art for this item.
 */
export async function iconUrl(itemId: string): Promise<ProbeResult> {
  const cached = iconCache.get(itemId);
  if (cached !== undefined) return cached;
  const inflight = inflightIcon.get(itemId);
  if (inflight) return inflight;

  const p = probeFirst([itemIconUrl(itemId)]).then((result) => {
    iconCache.set(itemId, result);
    inflightIcon.delete(itemId);
    return result;
  });
  inflightIcon.set(itemId, p);
  return p;
}

/**
 * Probe for `<item-id>/floor.png` adventure-scoped, return the URL or
 * `null` when the adventure doesn't ship art. URL is the same whether
 * mirroring is on; the renderer applies the flip in canvas / CSS via
 * {@link shouldMirrorFloor}.
 */
export async function floorUrl(itemId: string): Promise<ProbeResult> {
  const cached = floorCache.get(itemId);
  if (cached !== undefined) return cached;
  const inflight = inflightFloor.get(itemId);
  if (inflight) return inflight;

  const p = probeFirst([itemFloorUrl(itemId)]).then((result) => {
    floorCache.set(itemId, result);
    inflightFloor.delete(itemId);
    return result;
  });
  inflightFloor.set(itemId, p);
  return p;
}

/**
 * Fetch the optional per-item `item.json` manifest. Returns `null`
 * when no manifest is shipped for this item; caller falls back to
 * defaults.
 */
export async function loadItemSpriteManifest(itemId: string): Promise<ItemSpriteManifest | null> {
  const cached = manifestCache.get(itemId);
  if (cached !== undefined) return cached;
  const inflight = inflightManifest.get(itemId);
  if (inflight) return inflight;

  const fetcher = fetchOverride ?? defaultFetchManifest;
  const p = (async () => {
    const text = await fetcher(itemManifestUrl(itemId));
    if (text !== null) {
      try {
        const parsed = JSON.parse(text) as ItemSpriteManifest;
        manifestCache.set(itemId, parsed);
        inflightManifest.delete(itemId);
        return parsed;
      } catch {
        // malformed JSON — treat as missing
      }
    }
    manifestCache.set(itemId, null);
    inflightManifest.delete(itemId);
    return null;
  })();
  inflightManifest.set(itemId, p);
  return p;
}

/**
 * Whether the floor-drop renderer should apply a per-instance horizontal
 * flip for this item. Defaults to `true`; an item.json with
 * `floor.mirror = false` opts out.
 */
export async function shouldMirrorFloor(itemId: string): Promise<boolean> {
  const m = await loadItemSpriteManifest(itemId);
  return m?.floor?.mirror ?? DEFAULT_FLOOR_MIRROR;
}
